import React from 'react';
import { enrollUser, getAuction } from '../../../../models/auctions';
import { getLoggedUser } from '../../../../state/user';
import i18n from '../../../../i18n';
import { createThawaniCustomer, createThawaniSession } from '../../../../models/thawani';
import {
  thawaniPaymentUrl,
  thawaniConfig,
  paymentSuccessUrl,
  paymentCancelledUrl,
} from '../../../../constants/global';
import { Link, NavLink } from 'react-router-dom';
import logoThawani from '../../../../logoThawani.svg';
import { getUserInfo } from '../../../../models/users';
import { VisitNow } from './../../../home/helpers/visitNow';
import { checkBlackList } from '../../../../models/blacklist';
import HeaderBanner from './../../../common/HeaderBanner';
import {
  CreditCard,
  Building2,
  Wallet,
  Upload,
  FileText,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  CheckCircle,
  ShieldAlert,
  Ban,
  Eye,
  Info,
  Loader,
  Layers,
  Check,
  Plus,
  Loader2,
  X,
} from 'lucide-react';
import { OmrRiyal } from './../../../common/icons';
import { formatCurrency } from '../../../../state/utils';
import '../../../../styles/components/enroll-page.scss';
import { toast } from 'react-toastify';
import { fetchAcknowledgementPdf } from './services/enrollService';
import { openBlobInNewTab } from '../../../../utils/common';
import { CustomModal } from '../../../common/CustomModal';

// ─────────────────────────────────────────────────────────────
//  Constants
// ─────────────────────────────────────────────────────────────

const loggedInUser = getLoggedUser();

const PROFILE_FIELD_LABELS = {
  file_id_number: i18n.t('attach_id_no'),
  authority_name: i18n.t('auth_name'),
  bank: i18n.t('bank'),
  account_number: i18n.t('account_number'),
  cr_number: i18n.t('cr_number'),
  resident_card_number: i18n.t('personal_card_no'),
  vat_number: i18n.t('vat_number'),
  file_cr_number: i18n.t('attacth_cr_copy'),
  file_auth_letter: i18n.t('auth_letter'),
  file_vat_certificate: i18n.t('attach_vat'),
};

const OFFLINE_BANK_FIELDS = [
  {
    name: 'transfer_date',
    label: i18n.t('transfer_date') || 'Transfer Date',
    placeholder: '',
    type: 'date',
  },
  {
    name: 'receipt_number',
    label: i18n.t('receipt'),
    placeholder: i18n.t('please_enter_receipt_number'),
    type: 'text',
  },
  {
    name: 'bank',
    label: i18n.t('bank'),
    placeholder: i18n.t('please_enter_bank_name'),
    type: 'text',
  },
  {
    name: 'account_number',
    label: i18n.t('account_number'),
    placeholder: i18n.t('please_enter_bank_account'),
    type: 'text',
  },
  {
    name: 'beneficiary',
    label: i18n.t('beneficiary'),
    placeholder: i18n.t('please_enter_beneficiary_name'),
    type: 'text',
  },
];

const ONLINE_POLICY_POINTS = [
  'Deposit a Guarantee amount to participate.',
  'Refundable. Service charges: 1.5% local, 2% credit (non-refundable).',
  'Separate from item value.',
  'May vary per item.',
  'Not automatically returned.',
  'Returns to wallet for refund request.',
];

const MAX_FILE_SIZE_KB = 5120;
const PRICE_AMOUNT_MULTIPLIER = 1000;

// ─────────────────────────────────────────────────────────────
//  Helpers
// ─────────────────────────────────────────────────────────────

function getAuctionIdFromPath() {
  const path = window.location.pathname;
  return path.substring(path.lastIndexOf('/') + 1);
}

function isFileSizeValid(file) {
  return file.size / 1024 <= MAX_FILE_SIZE_KB;
}

function resolveDefaultPaymentState(groupInfo) {
  let paymentType = '';
  let requiresOnlinePayment = false;
  let showPaymentDetails = false;

  if (!groupInfo) return { paymentType, requiresOnlinePayment, showPaymentDetails };

  if (groupInfo.can_offline) {
    paymentType = 'offline';
  } else if (groupInfo.can_wallet) {
    paymentType = 'wallet';
  } else if (groupInfo.can_online) {
    paymentType = 'online';
    requiresOnlinePayment = true;
    showPaymentDetails = true;
  }

  return { paymentType, requiresOnlinePayment, showPaymentDetails };
}

function applyOnlineServiceFee(baseAmount, cardType) {
  const base = parseFloat(baseAmount);
  if (cardType === 'local') return base + (base * 1.5) / 100;
  if (cardType === 'credit') return base + (base * 2) / 100;
  return base;
}

// ─────────────────────────────────────────────────────────────
//  Component
// ─────────────────────────────────────────────────────────────

export class AuctionEnrollment extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      // UI toggles
      showTermsPanel: false,
      showBankPanel: true,
      showPaymentDetails: false,

      // Payment
      paymentType: '',
      selectedCardType: 'local',
      requiresOnlinePayment: false,
      selectedPriceUnits: 20000,

      // Form inputs
      formInput: { ...loggedInUser },
      isCompany: loggedInUser.is_company || 0,
      termsAccepted: false,
      receiptFile: null,

      // Auction
      auctionId: getAuctionIdFromPath(),
      auctionDetails: {},
      termsContent: '',

      // User / profile
      walletBalance: 0,
      isProfileComplete: true,
      missingProfileFields: [],
      isBlacklisted: false,

      // Enroll flow
      enrollData: [],
      thawaniCustomerId: '',
      isEnrolled: false,
      showSuccessMessage: false,
      rulesAccepted: false,

      // Loading states
      isPageLoading: false,
      isSubmitting: false,
      isPaymentRedirecting: false,

      // Receipt Upload
      receiptPreviewUrl: null,
      receiptFileError: null,
      isDraggingOver: false,

      // Acknowledge
      showAcknowledgementModal: false,
    };

    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleEnrollSubmit = this.handleEnrollSubmit.bind(this);
    this.createThawaniCustomer = this.createThawaniCustomer.bind(this);
    this.redirectToThawaniPayment = this.redirectToThawaniPayment.bind(this);
  }

  // ─── Lifecycle ─────────────────────────────────────────────

  componentDidMount() {
    window.scrollTo(0, 0);
    this.loadAuctionDetails();
    this.loadUserWalletInfo();
    this.loadBlacklistStatus();
  }

  componentWillUnmount() {
    if (this.state.receiptPreviewUrl) {
      URL.revokeObjectURL(this.state.receiptPreviewUrl);
    }
  }

  // ─── Data Loaders ──────────────────────────────────────────

  async loadAuctionDetails() {
    this.setState({ isPageLoading: true });

    const response = await getAuction(this.state.auctionId);
    if (!response) {
      this.setState({ isPageLoading: false });
      return;
    }

    const auction = response.data;
    const { paymentType, requiresOnlinePayment, showPaymentDetails } = resolveDefaultPaymentState(
      auction.group_info
    );

    const isOnlyOnline = auction.payment_type === 'Online';
    const acceptsOnlinePayment =
      auction.payment_type === 'Online' || auction.payment_type === 'Both';

    this.setState({
      auctionDetails: auction,
      termsContent: auction.terms,
      paymentType,
      requiresOnlinePayment: isOnlyOnline ? true : requiresOnlinePayment,
      showPaymentDetails: isOnlyOnline ? true : showPaymentDetails,
      selectedPriceUnits:
        acceptsOnlinePayment && auction.payment_amount
          ? auction.payment_amount * PRICE_AMOUNT_MULTIPLIER
          : this.state.selectedPriceUnits,
      isPageLoading: false,
    });
  }

  async loadUserWalletInfo() {
    const response = await getUserInfo(loggedInUser.id);
    if (!response?.success) return;

    const { data: userData, meta } = response;
    const profile = meta?.profile;

    loggedInUser.wallet_amount = userData.wallet_amount;
    localStorage.setItem('mzad_user_info', JSON.stringify(loggedInUser));

    const hasInsufficientBalance =
      Number(userData.wallet_amount) === 0 ||
      Number(userData.wallet_amount) < Number(this.state.auctionDetails.payment_amount);

    this.setState({
      walletBalance: userData.wallet_amount,
      isProfileComplete: !profile?.update_required,
      missingProfileFields: profile?.missing_fields ?? [],
      isNotEnoughWalletBalance: hasInsufficientBalance,
    });
  }

  async loadBlacklistStatus() {
    const response = await checkBlackList();
    if (response.success) {
      this.setState({ isBlacklisted: response.data.is_blacklisted });
    }
  }

  // ─── Event Handlers ────────────────────────────────────────

  handleInputChange(event) {
    const { name, value } = event.target;
    this.setState((prev) => ({
      formInput: { ...prev.formInput, [name]: value },
    }));
  }

  handleDragOver = (e) => {
    e.preventDefault();
    this.setState({ isDraggingOver: true });
  };

  handleDragLeave = () => {
    this.setState({ isDraggingOver: false });
  };

  handleDrop = (e) => {
    e.preventDefault();
    this.setState({ isDraggingOver: false });
    const file = e.dataTransfer.files[0];
    this.processReceiptFile(file);
  };

  handleFileChange = (event) => {
    const file = event.target.files[0];
    this.processReceiptFile(file);
  };

  processReceiptFile(file) {
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      this.setState({ receiptFileError: 'Invalid file type. Use JPG, PNG or PDF.' });
      return;
    }

    if (!isFileSizeValid(file)) {
      this.setState({ receiptFileError: 'File too large. Maximum allowed size is 5 MB.' });
      return;
    }

    if (this.state.receiptPreviewUrl) {
      URL.revokeObjectURL(this.state.receiptPreviewUrl);
    }

    const previewUrl = URL.createObjectURL(file);
    this.setState({
      receiptFile: file,
      receiptPreviewUrl: previewUrl,
      receiptFileError: null,
    });
  }

  removeReceiptFile = () => {
    if (this.state.receiptPreviewUrl) {
      URL.revokeObjectURL(this.state.receiptPreviewUrl);
    }
    this.setState({
      receiptFile: null,
      receiptPreviewUrl: null,
      receiptFileError: null,
    });
  };

  openReceiptPreview = () => {
    if (this.state.receiptPreviewUrl) {
      window.open(this.state.receiptPreviewUrl, '_blank');
    }
  };

  formatFileSize(bytes) {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return Math.round(bytes / 1024) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
  }

  handlePaymentTypeChange(newPaymentType) {
    const isOnline = newPaymentType === 'online';

    if (newPaymentType === 'wallet') {
      this.loadUserWalletInfo();
    }

    this.setState({
      paymentType: newPaymentType,
      requiresOnlinePayment: isOnline,
      showPaymentDetails: isOnline,
    });
  }

  // ─── Enroll Submit ─────────────────────────────────────────

  async handleEnrollSubmit(event) {
    event.preventDefault();

    if (!this.validateOfflineForm()) return;

    const { auctionDetails, paymentType, selectedCardType } = this.state;
    const baseAmount = auctionDetails.payment_amount;

    const finalAmount =
      paymentType === 'online' ? applyOnlineServiceFee(baseAmount, selectedCardType) : baseAmount;

    this.setState({
      selectedPriceUnits: finalAmount * PRICE_AMOUNT_MULTIPLIER,
      isSubmitting: true,
    });

    const formData = this.buildEnrollFormData(baseAmount);

    this.setState({ isPaymentRedirecting: true });
    const response = await enrollUser(formData);

    if (response.success) {
      this.setState({ showSuccessMessage: true, isSubmitting: false });

      if (paymentType === 'online') {
        this.setState({ showSuccessMessage: false });
        this.createThawaniCustomer(response.data);
        this.setState({ isEnrolled: true, enrollData: response.data });
      }
    } else {
      this.setState({ isSubmitting: false, isPaymentRedirecting: false });
      toast.error(response.message);
    }
  }

  validateOfflineForm() {
    if (this.state.paymentType !== 'offline') return true;

    const { bank, account_number, beneficiary, receipt_number, transfer_date } =
      this.state.formInput;
    const allFieldsFilled =
      bank &&
      bank !== 'null' &&
      account_number &&
      account_number !== 'null' &&
      beneficiary &&
      beneficiary !== 'null' &&
      receipt_number &&
      receipt_number !== 'null' &&
      transfer_date &&
      transfer_date !== 'null' &&
      this.state.receiptFile !== null;

    if (!allFieldsFilled) {
      toast.error(i18n.t('all_mandatory'));
    }

    return allFieldsFilled;
  }

  buildEnrollFormData(paymentAmount) {
    const { auctionId, formInput, isCompany, requiresOnlinePayment, paymentType, receiptFile } =
      this.state;

    const fd = new FormData();
    fd.append('auction', auctionId);
    fd.append('enroll_name', formInput.name);
    fd.append('bank', formInput.bank);
    fd.append('account_number', formInput.account_number);
    fd.append('beneficiary', formInput.beneficiary);
    fd.append('receipt_number', formInput.receipt_number);
    fd.append('transfer_date', formInput.transfer_date || '');
    fd.append('is_company', Boolean(isCompany));
    fd.append('is_offline', !requiresOnlinePayment);
    fd.append('ptype', paymentType);
    fd.append('identity_type', 'Civil Card');
    fd.append('amount', paymentAmount);

    if (receiptFile) {
      fd.append('file_receipt', receiptFile, receiptFile.name);
    }

    return fd;
  }

  // ─── Thawani Payment Flow ───────────────────────────────────

  async createThawaniCustomer(enrollData) {
    window.scrollTo(0, 0);

    const clientId = loggedInUser.email || loggedInUser.username;
    if (!loggedInUser || !clientId) return;

    const thawaniResponse = await createThawaniCustomer({ client_customer_id: clientId });

    if (thawaniResponse?.data) {
      localStorage.setItem('thawani_customer_id', thawaniResponse.data.id);
      this.setState({ thawaniCustomerId: thawaniResponse.data.id });
      this.redirectToThawaniPayment();
    }
  }

  async redirectToThawaniPayment() {
    const { enrollData, thawaniCustomerId, auctionId, selectedPriceUnits } = this.state;
    const amountInOmr = Number(selectedPriceUnits) / PRICE_AMOUNT_MULTIPLIER;

    const queryParams = `?gid=${enrollData.group_id}&eid=${enrollData.id}&auc=${auctionId}&amt=${amountInOmr}`;

    const sessionPayload = {
      client_reference_id: loggedInUser.email,
      mode: 'payment',
      products: [
        { name: 'Mzadcom Auction', quantity: 1, unit_amount: parseInt(selectedPriceUnits) },
      ],
      success_url: paymentSuccessUrl + queryParams,
      cancel_url: paymentCancelledUrl + queryParams,
      customer_id: thawaniCustomerId,
      metadata: {
        'Customer name': loggedInUser.name,
        'Order id': enrollData.id,
        user: loggedInUser.id,
        auction: auctionId,
      },
    };

    const sessionResponse = await createThawaniSession(sessionPayload);
    this.setState({ isPaymentRedirecting: false });

    const sessionId = sessionResponse?.data?.session_id;
    const sessionFailed = sessionResponse?.data?.success === false;

    if (!sessionFailed && sessionId) {
      localStorage.setItem('invoice', sessionResponse.data.invoice);
      window.location.href = `${thawaniPaymentUrl}${sessionId}?key=${thawaniConfig.publishable_key}`;
    }
  }

  // ─── Render Helpers ────────────────────────────────────────

  get isSubmitDisabled() {
    const {
      termsAccepted,
      rulesAccepted,
      isEnrolled,
      isProfileComplete,
      paymentType,
      isNotEnoughWalletBalance,
      isBlacklisted,
      auctionDetails,
    } = this.state;

    const isVisitRequired =
      Number(auctionDetails.visit_amount) > 0 && auctionDetails.is_visit_active;

    return (
      !termsAccepted ||
      !rulesAccepted ||
      isEnrolled ||
      !isProfileComplete ||
      (paymentType === 'wallet' && isNotEnoughWalletBalance) ||
      auctionDetails.is_enrolled ||
      isVisitRequired ||
      isBlacklisted
    );
  }

  get canShowSubmitSection() {
    const { paymentType, isNotEnoughWalletBalance } = this.state;
    return !(paymentType === 'wallet' && isNotEnoughWalletBalance);
  }

  get localizedAuctionTitle() {
    const lang = localStorage.getItem('I18N_LANGUAGE');
    return lang === 'ar' ? this.state.auctionDetails.title_ar : this.state.auctionDetails.title;
  }

  get localizedGroupName() {
    const lang = localStorage.getItem('I18N_LANGUAGE');
    const groupInfo = this.state.auctionDetails?.group_info;
    if (!groupInfo) return null;
    return lang === 'ar'
      ? groupInfo.group_name_ar || groupInfo.group_name
      : groupInfo.group_name || groupInfo.group_name_ar;
  }
  handleAcknowledgementView = async () => {
    const { auctionDetails } = this.state;
    const lang = localStorage.getItem('I18N_LANGUAGE') ?? 'en';

    this.setState({ isAcknowledgementLoading: true });

    try {
      const blob = await fetchAcknowledgementPdf(auctionDetails.id, lang);
      openBlobInNewTab(blob);
    } catch (error) {
      console.error('[AcknowledgementView]', error);
      toast.error(this.props.t('errors.acknowledgement_pdf_unavailable'));
    } finally {
      this.setState({ isAcknowledgementLoading: false });
    }
  };

  // ─── Sub-renderers ─────────────────────────────────────────

  renderAuctionInfoCard() {
    const { auctionDetails } = this.state;

    return (
      <div className="mze-panel">
        <div className="mze-panel__header">
          <Info size={16} />
          <span>{i18n.t('auction_details') || 'Auction Details'}</span>
        </div>
        <div className="mze-panel__body">
          <div className="mze-auction-info">
            <div className="mze-auction-info__left">
              <span className="mze-auction-info__id">MZAD-{auctionDetails.id}</span>
              <h3 className="mze-auction-info__title">{this.localizedAuctionTitle}</h3>
              {this.localizedGroupName && (
                <span className="mze-auction-info__group">
                  <Layers size={12} />
                  {this.localizedGroupName}
                </span>
              )}
            </div>
            {auctionDetails.payment_amount && (
              <div className="mze-auction-info__right">
                <span className="mze-auction-info__label">{i18n.t('enroll_amount')}</span>
                <span className="mze-auction-info__amount">
                  {formatCurrency(auctionDetails.payment_amount)}{' '}
                  <OmrRiyal width={18} height={18} />
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  renderAlerts() {
    const { isBlacklisted, isProfileComplete, missingProfileFields, auctionDetails } = this.state;
    const isVisitRequired =
      Number(auctionDetails.visit_amount) > 0 && auctionDetails.is_visit_active;

    return (
      <>
        {isBlacklisted && (
          <div className="mze-alert mze-alert--danger">
            <Ban size={18} className="mze-alert__icon" />
            <div className="mze-alert__content">
              <strong>{i18n.t('blocked') || 'Blocked'}</strong>
              <p>{i18n.t('user_blacklisted_message')}</p>
            </div>
          </div>
        )}

        {!isProfileComplete && (
          <div className="mze-alert mze-alert--warning">
            <ShieldAlert size={18} className="mze-alert__icon" />
            <div className="mze-alert__content">
              <strong>{i18n.t('profile_incomplete') || 'Profile Incomplete'}</strong>
              <p>{i18n.t('profile_completion_message')}</p>
              <div className="mze-alert__badges">
                {missingProfileFields.map((fieldKey, index) => (
                  <span key={index} className="mze-missing-badge">
                    {PROFILE_FIELD_LABELS[fieldKey]}
                  </span>
                ))}
              </div>
              <Link to="/profile" className="mze-alert__link">
                {i18n.t('complete_profile') || 'Complete Profile'} →
              </Link>
            </div>
          </div>
        )}

        {isVisitRequired && (
          <div className="mze-alert mze-alert--danger">
            <AlertCircle size={18} className="mze-alert__icon" />
            <div className="mze-alert__content">
              <p>
                {auctionDetails.is_visit_active
                  ? i18n.t('visit_warning_message')
                  : i18n.t('visit_warning_message_np')}
              </p>
              {auctionDetails.visit_amount && auctionDetails.is_visit_active && (
                <VisitNow
                  amount={auctionDetails.visit_amount}
                  type="sitevisit"
                  id={auctionDetails.id}
                  gid={auctionDetails.group}
                  is_group={auctionDetails.is_grouped}
                />
              )}
            </div>
          </div>
        )}

        {auctionDetails.is_enrolled && (
          <div className="mze-alert mze-alert--success">
            <CheckCircle size={18} className="mze-alert__icon" />
            <div className="mze-alert__content">
              <strong>{i18n.t('enrolled') || 'Enrolled'}</strong>
              <p>{i18n.t('enrolled_message')}</p>
              <Link to="/" className="mze-alert__link mze-alert__link--success">
                {i18n.t('home')} →
              </Link>
            </div>
          </div>
        )}
      </>
    );
  }

  renderPaymentTypeTabs() {
    const { auctionDetails, paymentType } = this.state;
    const groupInfo = auctionDetails?.group_info;

    return (
      <div className="mze-tabs">
        {groupInfo?.can_offline && (
          <button
            className={`mze-tab ${paymentType === 'offline' ? 'mze-tab--active' : ''}`}
            onClick={() => this.handlePaymentTypeChange('offline')}
          >
            <Building2 size={16} />
            <span>{i18n.t('bank_transfer')}</span>
          </button>
        )}
        {groupInfo?.can_online && (
          <button
            className={`mze-tab ${paymentType === 'online' ? 'mze-tab--active' : ''}`}
            onClick={() => this.handlePaymentTypeChange('online')}
          >
            <CreditCard size={16} />
            <span>{i18n.t('online')}</span>
          </button>
        )}
        {groupInfo?.can_wallet && (
          <button
            className={`mze-tab ${paymentType === 'wallet' ? 'mze-tab--active' : ''}`}
            onClick={() => this.handlePaymentTypeChange('wallet')}
          >
            <Wallet size={16} />
            <span>{i18n.t('wallet')}</span>
          </button>
        )}
      </div>
    );
  }

  renderOfflinePaymentForm() {
    const {
      isProfileComplete,
      formInput,
      receiptFile,
      receiptPreviewUrl,
      receiptFileError,
      isDraggingOver,
    } = this.state;

    const today = new Date().toISOString().split('T')[0];
    const isPdf = receiptFile?.type === 'application/pdf';
    const isImage = receiptFile?.type?.startsWith('image/');

    return (
      <div className="mze-form-block">
        <div className="mze-policy">
          <strong className="mze-policy__title">{i18n.t('guarantee_amount_policy_head')}</strong>
          <div className="mze-policy__text">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
              <p key={n}>{i18n.t(`guarantee_amount_policy_${n}`)}</p>
            ))}
          </div>
        </div>

        {/* ── Receipt Upload ── */}
        <div className="mze-field mze-field--upload">
          <label className="mze-field__label">
            <Upload size={14} />
            {i18n.t('receipt')}
            <span className="mze-field__required">*</span>
          </label>

          <input
            type="file"
            id="file_receipt"
            name="file_receipt"
            accept=".jpg,.jpeg,.png,.pdf"
            style={{ display: 'none' }}
            disabled={!isProfileComplete}
            onChange={this.handleFileChange}
          />

          {!receiptFile ? (
            <div
              className={`mze-upload-zone ${isDraggingOver ? 'mze-upload-zone--dragover' : ''} ${!isProfileComplete ? 'mze-upload-zone--disabled' : ''}`}
              onDragOver={this.handleDragOver}
              onDragLeave={this.handleDragLeave}
              onDrop={this.handleDrop}
            >
              <div className="mze-upload-zone__icon">
                <Upload size={24} />
              </div>
              <p className="mze-upload-zone__title">Drop your receipt here</p>
              <p className="mze-upload-zone__sub">JPG, PNG or PDF — max 5 MB</p>
              <button
                type="button"
                className="mze-upload-zone__btn"
                disabled={!isProfileComplete}
                onClick={() => document.getElementById('file_receipt').click()}
              >
                Browse file
              </button>
            </div>
          ) : (
            <div className="mze-receipt-preview">
              <div className="mze-receipt-preview__header">
                <FileText size={15} className="mze-receipt-preview__file-icon" />
                <span className="mze-receipt-preview__name">{receiptFile.name}</span>
                <span className="mze-receipt-preview__size">
                  {this.formatFileSize(receiptFile.size)}
                </span>
                <button
                  type="button"
                  className="mze-receipt-preview__remove"
                  onClick={this.removeReceiptFile}
                >
                  ✕ Remove
                </button>
              </div>

              <div
                className={`mze-receipt-preview__body ${isImage ? 'mze-receipt-preview__body--image' : ''}`}
              >
                {isImage && (
                  <img
                    src={receiptPreviewUrl}
                    alt="Receipt preview"
                    className="mze-receipt-preview__img"
                  />
                )}
                {isPdf && (
                  <div className="mze-receipt-preview__pdf">
                    <FileText size={32} className="mze-receipt-preview__pdf-icon" />
                    <div>
                      <p className="mze-receipt-preview__pdf-name">{receiptFile.name}</p>
                      <span className="mze-receipt-preview__pdf-meta">
                        PDF document · {this.formatFileSize(receiptFile.size)}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              <div className="mze-receipt-preview__actions">
                <button
                  type="button"
                  className="mze-receipt-preview__view-btn"
                  onClick={this.openReceiptPreview}
                >
                  <Eye size={13} /> View full size
                </button>
                <button
                  type="button"
                  className="mze-receipt-preview__change-btn"
                  onClick={() => document.getElementById('file_receipt').click()}
                >
                  <Upload size={13} /> Change file
                </button>
              </div>
            </div>
          )}

          {receiptFileError && (
            <div className="mze-upload-error">
              <AlertCircle size={13} />
              {receiptFileError}
            </div>
          )}

          {receiptFile && !receiptFileError && (
            <div className="mze-upload-success">
              <CheckCircle size={13} />
              Receipt uploaded successfully.
            </div>
          )}
        </div>

        {/* ── Bank Fields ── */}
        <div className="mze-offline-fields">
          {OFFLINE_BANK_FIELDS.map((field) => (
            <div
              className={`mze-field ${field.name === 'beneficiary' ? 'mze-field--full' : ''}`}
              key={field.name}
            >
              <label className="mze-field__label">
                <span className="mze-field__required">*</span>
                {field.label}
              </label>
              <input
                className="mze-field__input"
                type={field.type}
                name={field.name}
                placeholder={field.placeholder}
                max={field.type === 'date' ? today : undefined}
                value={formInput[field.name] || ''}
                onChange={this.handleInputChange}
              />
            </div>
          ))}
        </div>
      </div>
    );
  }

  renderOnlinePaymentForm() {
    return (
      <div className="mze-form-block">
        <div className="mze-policy">
          <strong className="mze-policy__title">
            {i18n.t('online_deposit_and_refund_policy')}
          </strong>
          <div className="mze-policy__text">
            {ONLINE_POLICY_POINTS.map((point, index) => (
              <p key={index}>
                {index + 1}) {point}
              </p>
            ))}
          </div>
        </div>
      </div>
    );
  }

  renderWalletPaymentForm() {
    const { walletBalance, isNotEnoughWalletBalance } = this.state;

    return (
      <div className="mze-form-block">
        <div className="mze-wallet-bar">
          <Wallet size={18} className="mze-wallet-bar__icon" />
          <span className="mze-wallet-bar__text">
            {i18n.t('balance')}:
            <strong className="mze-wallet-bar__amount">
              {formatCurrency(walletBalance)} {i18n.t('omr')}
            </strong>
          </span>
        </div>

        {isNotEnoughWalletBalance && (
          <>
            <div className="mze-alert mze-alert--danger mze-alert--compact">
              <AlertCircle size={16} className="mze-alert__icon" />
              <span>{i18n.t('insufficient_wallet_balance')}</span>
            </div>
            <NavLink to="/wallet-manage" className="mze-add-fund">
              <Plus size={18} />
              <span>{i18n.t('add_fund')}</span>
            </NavLink>
          </>
        )}
      </div>
    );
  }

  renderThawaniSelector() {
    const { showPaymentDetails, selectedCardType } = this.state;
    if (!showPaymentDetails) return null;

    return (
      <div className="mze-thawani">
        <img src={logoThawani} alt="Thawani" className="mze-thawani__logo" />
        <p className="mze-thawani__text">{i18n.t('redirect_to_thawani')}</p>
        <div className="mze-card-types">
          <button
            className={`mze-card-type ${selectedCardType === 'local' ? 'mze-card-type--active' : ''}`}
            onClick={() => this.setState({ selectedCardType: 'local' })}
          >
            {i18n.t('local_cards_ecommended')}
          </button>
          <button
            className={`mze-card-type ${selectedCardType === 'credit' ? 'mze-card-type--active' : ''}`}
            onClick={() => this.setState({ selectedCardType: 'credit' })}
          >
            {i18n.t('credit_cards_coast_2')}
          </button>
        </div>
      </div>
    );
  }

  renderSubmitSection() {
    const {
      termsAccepted,
      rulesAccepted,
      isEnrolled,
      isSubmitting,
      isPaymentRedirecting,
      showSuccessMessage,
      auctionId,
      isAcknowledgementLoading,
      auctionDetails,
    } = this.state;

    return (
      <>
        {this.canShowSubmitSection && (
          <div className="mze-submit-area">
            <div className="mze-rules-row">
              <label className="mze-check">
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={() => this.setState({ termsAccepted: !termsAccepted })}
                />
                <span className="mze-check__box">
                  {termsAccepted && <Check size={12} strokeWidth={3} />}
                </span>
                <span className="mze-check__text">
                  {i18n.t('agree_to_terms')} <span className="mze-field__required">*</span>
                </span>
              </label>

              <div className="mze-rules-actions">
                <button
                  type="button"
                  className="mze-rules-btn"
                  onClick={() => {
                    if (!auctionDetails.file_terms) {
                      toast.error('No files found. Contact admin.');

                      return;
                    }
                    window.open(auctionDetails.file_terms, '_blank');
                  }}
                >
                  <Eye size={14} />
                  <span>{i18n.t('preview') || 'Preview'}</span>
                </button>
              </div>
            </div>

            <div className="mze-rules-row">
              <label
                className="mze-check"
                onClick={(e) => {
                  e.preventDefault();
                  if (!rulesAccepted) {
                    this.setState({ showAcknowledgementModal: true });
                  } else {
                    this.setState({ rulesAccepted: false });
                  }
                }}
              >
                <input type="checkbox" checked={rulesAccepted} onChange={() => {}} />
                <span className="mze-check__box">
                  {rulesAccepted && <Check size={12} strokeWidth={3} />}
                </span>
                <span className="mze-check__text">
                  {i18n.t('acknowledge_auction_rules') || 'I acknowledge the auction rules'}
                  <span className="mze-field__required">*</span>
                </span>
              </label>

              {rulesAccepted && (
                <div className="mze-rules-actions">
                  <button
                    type="button"
                    className="mze-rules-btn"
                    onClick={this.handleAcknowledgementView}
                    disabled={isAcknowledgementLoading}
                  >
                    {isAcknowledgementLoading ? (
                      <Loader2 size={16} className="mze-submit__spinner" />
                    ) : (
                      <Eye size={14} />
                    )}
                    <span>{i18n.t('preview') || 'Preview'}</span>
                  </button>
                </div>
              )}
            </div>

            <button
              type="button"
              className="mze-submit"
              onClick={this.handleEnrollSubmit}
              disabled={this.isSubmitDisabled}
            >
              {isEnrolled ? (
                <>
                  <Loader size={16} className="mze-submit__spinner" /> Loading...
                </>
              ) : (
                i18n.t('save_continue')
              )}
            </button>
          </div>
        )}

        {(isSubmitting || isPaymentRedirecting) && (
          <div className="mze-loader">
            <Loader size={24} className="mze-loader__icon" />
          </div>
        )}

        {!isSubmitting && showSuccessMessage && (
          <div className="mze-alert mze-alert--success mze-alert--compact">
            <CheckCircle size={18} className="mze-alert__icon" />
            <div className="mze-alert__content">
              Enrollment completed! Go to{' '}
              <Link
                to={`/auction-details/${auctionId}`}
                className="mze-alert__link mze-alert__link--success"
              >
                Auction
              </Link>{' '}
              page and start bidding.
            </div>
          </div>
        )}
      </>
    );
  }

  // ─── Add this new method ───────────────────────────────────
  renderAcknowledgementModal() {
    const { showAcknowledgementModal, auctionDetails } = this.state;
    const clientName = auctionDetails?.organizationDetails?.organization_name || '—';
    const groupName = this.localizedGroupName || '—';

    const terms = [i18n.t('ack_term_1'), i18n.t('ack_term_2'), i18n.t('ack_term_3')];

    const footer = (
      <div className="mze-ack-footer">
        <button
          type="button"
          className="modal-btn btn-cancel"
          onClick={() => this.setState({ showAcknowledgementModal: false, rulesAccepted: false })}
        >
          <X size={14} />
          <span>{i18n.t('decline')}</span>
        </button>
        <button
          type="button"
          className="modal-btn btn-confirm"
          onClick={() => this.setState({ showAcknowledgementModal: false, rulesAccepted: true })}
        >
          <Check size={14} />
          <span>{i18n.t('accept_confirm')}</span>
        </button>
      </div>
    );

    return (
      <CustomModal
        isOpen={showAcknowledgementModal}
        header={i18n.t('auction_agreement_acknowledgement')}
        headerIcon={<FileText size={18} />}
        footer={footer}
        modalClassName="mze-ack-modal"
        closeBtn={false}
      >
        {/* ── Enrollment confirmation ── */}
        <div className="mze-ack-block">
          <div className="mze-ack-block__head">
            <FileText size={13} />
            <span>{i18n.t('enrollment_confirmation')}</span>
          </div>
          <div className="mze-ack-block__body">
            <p className="mze-ack-intro">
              {i18n.t('ack_intro') ||
                'I hereby confirm that I have successfully enrolled for the following auction:'}
            </p>
            <div className="mze-ack-table">
              <div className="mze-ack-row">
                <span className="mze-ack-row__key">{i18n.t('client_name')}</span>
                <span className="mze-ack-row__val">{clientName}</span>
              </div>
              <div className="mze-ack-row">
                <span className="mze-ack-row__key">{i18n.t('group_name')}</span>
                <span className="mze-ack-row__val">{groupName}</span>
              </div>
            </div>
          </div>
        </div>

        {/* ── Terms ── */}
        <div className="mze-ack-block">
          <div className="mze-ack-block__head">
            <FileText size={13} />
            <span>{i18n.t('acknowledgement_of_terms')}</span>
          </div>
          <div className="mze-ack-block__body mze-ack-block__body--terms">
            {terms.map((term, idx) => (
              <div key={idx} className="mze-ack-term">
                <div className="mze-ack-term__num">{idx + 1}</div>
                <p className="mze-ack-term__text">{term}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ── Legal notice ── */}
        <div className="mze-ack-notice">
          <AlertCircle size={14} />
          <p>{i18n.t('ack_legal_notice')}</p>
        </div>
      </CustomModal>
    );
  }
  renderPaymentPanel() {
    const { isProfileComplete, paymentType } = this.state;
    if (!isProfileComplete) return null;

    return (
      <div className="mze-panel">
        <div className="mze-panel__header">
          <Wallet size={16} />
          <span>{i18n.t('pay_type') || 'Payment Method'}</span>
        </div>
        <div className="mze-panel__body">
          {this.renderPaymentTypeTabs()}
          {paymentType === 'offline' && this.renderOfflinePaymentForm()}
          {paymentType === 'online' && this.renderOnlinePaymentForm()}
          {paymentType === 'wallet' && this.renderWalletPaymentForm()}
          {this.renderThawaniSelector()}
          {this.renderSubmitSection()}
        </div>
      </div>
    );
  }

  renderBankDetailsPanel() {
    const { showBankPanel, auctionDetails } = this.state;

    return (
      <div className="mze-panel mze-panel--collapsible">
        <button
          className="mze-panel__toggle"
          onClick={() => this.setState({ showBankPanel: !showBankPanel })}
        >
          <span className="mze-panel__toggle-left">
            <Building2 size={16} />
            {i18n.t('bank_details')}
          </span>
          {showBankPanel ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>

        {showBankPanel && (
          <div className="mze-panel__body">
            {auctionDetails.is_direct_sale ? (
              <>
                <div className="mze-bank-card">
                  <p>Bank Name : Bank Muscat</p>
                  <p>Account Name : eco SPC</p>
                  <p>Account Number : 0423076966320012</p>
                  <p>IBAN : OM320270423076966320012</p>
                  <p>SWIFT CODE : BMUSOMRXXXX</p>
                  <p>Currency : OMR</p>
                </div>
                <div className="mze-bank-card mze-bank-card--mt">
                  <p>Bank Name : Bank Muscat</p>
                  <p>Account Name : eco SPC</p>
                  <p>Account Number : 0423076966320028</p>
                  <p>IBAN : OM850270423076966320028</p>
                  <p>Currency : USD</p>
                </div>
              </>
            ) : (
              <div className="mze-bank-card">
                <p>Bank Name : Bank Muscat</p>
                <p>Account Name : Mzadcom Smart Auction Solutions LLC</p>
                <p>Account Number : 0440061839220015</p>
                <p>IBAN : OM360270440061839220015</p>
                <p>SWIFT CODE : BMUSOMRX</p>
                <p>Currency : OMANI RIYAL</p>
                <p>Bank/Branch : Gala.</p>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // ─── Main Render ───────────────────────────────────────────

  render() {
    return (
      <React.Fragment>
        <div className="mze-page">
          <HeaderBanner
            title={i18n.t('auction_enrollment_payments')}
            subtitle={i18n.t('manage_and_view_enrollment_payments')}
          />
          <div className="mze-container">
            <div className="mze-grid">
              <div className="mze-left">
                {this.renderAuctionInfoCard()}
                {this.renderAlerts()}
                {this.renderPaymentPanel()}
              </div>
              <div className="mze-right">{this.renderBankDetailsPanel()}</div>
            </div>
          </div>
        </div>
        {this.renderAcknowledgementModal()}
      </React.Fragment>
    );
  }
}
